/**
 * Seasonal merchandising config — the single place to control festival collections.
 *
 * HOW TO USE
 *   force: null   → the season follows the start/end dates below (normal setting)
 *   force: true   → season is always ON,  whatever the date (use to launch early / test)
 *   force: false  → season is always OFF, whatever the date (use to kill it mid-season)
 *
 * Dates are inclusive and interpreted in India time (IST). `end` should be the last
 * day you want the collection visible — usually a day or two after visarjan, so late
 * orders and returning visitors still land on it.
 *
 * Each year, update `start` and `end` to the new festival dates and set `force` back
 * to null. Nothing else needs changing.
 */

export const SEASONS = {
  ganesh: {
    key: "ganesh",
    label: "Ganesh Chaturthi",
    // Shown on the Shop page collection band.
    collectionTitle: "Ganesh Chaturthi Collection",
    collectionBlurb:
      "Eco-friendly clay Ganesh idols and complete puja kits, made in our Indore workshop. Natural, biodegradable, and ready for a greener visarjan.",
    badge: "Festive Collection",

    // Confirmed season window (IST, inclusive).
    start: "2026-08-12",
    end: "2026-09-25",

    // Dates are confirmed, so the season now switches itself on and off.
    // Set to true to force it on, or false to pull it early.
    force: null,
  },

  diwali: {
    key: "diwali",
    label: "Diwali",
    collectionTitle: "Diwali Gifting Collection",
    collectionBlurb:
      "Curated hampers of incense, dhoop and candles — ready to gift, straight from our factory.",
    badge: "Festive Collection",

    // ⚠️ CONFIRM THESE DATES.
    start: "2026-10-25",
    end: "2026-11-15",

    force: false,
  },
};

/** Parse "YYYY-MM-DD" as a date at IST midnight, avoiding local-timezone drift. */
function parseISTDate(value, endOfDay = false) {
  const [year, month, day] = value.split("-").map(Number);
  // IST is UTC+5:30, so IST midnight is 18:30 UTC the previous day.
  const utcMs = Date.UTC(year, month - 1, day, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0);
  return new Date(utcMs - 5.5 * 60 * 60 * 1000);
}

/**
 * Is a given season currently running?
 * `force` always wins; otherwise the date window decides.
 */
export function isSeasonActive(key, now = new Date()) {
  const season = SEASONS[key];
  if (!season) return false;
  if (season.force === true) return true;
  if (season.force === false) return false;
  if (!season.start || !season.end) return false;

  return now >= parseISTDate(season.start) && now <= parseISTDate(season.end, true);
}

/** The season to feature right now, or null when nothing is running. */
export function getActiveSeason(now = new Date()) {
  const active = Object.values(SEASONS).find((season) => isSeasonActive(season.key, now));
  return active || null;
}

/** True when a product belongs to a season that is not currently running. */
export function isHiddenSeasonalProduct(item, now = new Date()) {
  if (item.isGaneshSeasonal && !isSeasonActive("ganesh", now)) return true;
  if (item.isDiwaliSeasonal && !isSeasonActive("diwali", now)) return true;
  return false;
}

/** Which season a product belongs to, or null for everyday stock. */
export function getProductSeason(item) {
  if (item.isGaneshSeasonal) return "ganesh";
  if (item.isDiwaliSeasonal) return "diwali";
  return null;
}

/**
 * Filter tagged content (blog posts etc.) down to what should be visible today.
 * Items carry an explicit `season` field; untagged items are evergreen and always
 * shown. Replaces the old approach of scanning post text for the word "ganesh".
 */
export function filterSeasonalContent(items = [], now = new Date()) {
  return items.filter((item) => !item.season || isSeasonActive(item.season, now));
}
